import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, Trash2, Upload, Loader2, X, Waypoints, TriangleAlert, Save, Sparkles, Undo2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { planningService } from '../../services/planningService';
import UploadVolumesModal from './UploadVolumesModal';
import LaneVolumesModal from './LaneVolumesModal';
import LaneReturnsModal from './LaneReturnsModal';
import OptimiseModal from './OptimiseModal';

// =============================================================================
// Planning > Network
//
// The map IS the model. Nodes are sites, edges are lanes, and the whole page
// answers one question per node:
//
//     production + inbound  -  outbound - local demand  =  balance
//
// A negative balance is a site the plan cannot supply. Showing it on the node,
// where the planner is already looking, is why this is a graph and not another
// grid — "lift coastal demand, forget the trunk leg feeding it" is a red badge
// here rather than a discrepancy someone finds in month seven.
//
// TWO LANE TYPES:
//   NORMAL   finished goods out, planned by SKU
//   RETURNS  returnable packaging and quality rejects coming back off trade,
//            planned as a plain volume — nobody budgets which SKU comes back
//
// NO VEHICLES, DISTANCES OR RATES HERE. Whether a truck returns empty is a
// distribution decision made on the rating page. Planning decides what moves;
// distribution decides what it costs to move it.
//
// LAYOUT IS PER BUDGET VERSION (bud_site_positions) — a scenario is often
// exactly where the network shape changes, so a shared layout fights itself.
// =============================================================================

const NODE_W = 176;
const NODE_H = 74;
const TIER_X = 260;
const ROW_Y = 118;
const PAD = 70;

const TYPE_STYLE = {
  PRODUCTION: { fill: '#059669', label: 'Plant' },
  EXTERNAL: { fill: '#7c3aed', label: 'External' },
  CROSSDOCK: { fill: '#d97706', label: 'Cross-dock' },
  DEPOT: { fill: '#0284c7', label: 'Depot' },
  STORE: { fill: '#475569', label: 'Store' },
  OTHER: { fill: '#64748b', label: 'Other' },
};
const styleOf = (t) => TYPE_STYLE[t] || TYPE_STYLE.OTHER;

const LANE_COLOUR = { NORMAL: '#64748b', RETURNS: '#d97706' };
const LANE_COLOUR_EMPTY = { NORMAL: '#cbd5e1', RETURNS: '#fcd34d' };

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const btnAmber = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';

const autoLayout = (sites) => {
  const perTier = {};
  return sites.map((s) => {
    const tier = s.map_tier ?? 9;
    perTier[tier] = (perTier[tier] || 0);
    const row = perTier[tier]++;
    return {
      ...s,
      map_x: s.map_x ?? PAD + tier * TIER_X,
      map_y: s.map_y ?? PAD + row * ROW_Y,
    };
  });
};

export default function NetworkPlanning() {
  const { selectedSite, selectedVersion, fyStartMonth } = useAppContext();
  const svgRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [sites, setSites] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [flow, setFlow] = useState({});
  const [capacityUom, setCapacityUom] = useState('PALLET');

  const [selectedLane, setSelectedLane] = useState(null);
  const [drawFrom, setDrawFrom] = useState(null);
  const [drawType, setDrawType] = useState(null); // 'NORMAL' | 'RETURNS' while drawing
  const [showUpload, setShowUpload] = useState(false);
  const [showOptimise, setShowOptimise] = useState(false);
  const [openLane, setOpenLane] = useState(null); // lane whose volume popup is open
  const [dirtyLayout, setDirtyLayout] = useState(false);
  const dragRef = useRef(null);

  const readOnly = !!selectedSite;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await planningService.getNetwork();
      const d = res.data || {};
      setSites(autoLayout((d.sites || []).filter((s) => s.in_network)));
      setLanes(d.lanes || []);
      setFlow(d.flow || {});
      setCapacityUom(d.capacityUom || 'PALLET');
      setDirtyLayout(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the network.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reloads on version change: positions and lanes are both version-scoped.
  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const siteByCode = useMemo(() => Object.fromEntries(sites.map((s) => [s.site_code, s])), [sites]);

  const extent = useMemo(() => {
    const maxX = sites.reduce((a, s) => Math.max(a, s.map_x + NODE_W), 0);
    const maxY = sites.reduce((a, s) => Math.max(a, s.map_y + NODE_H), 0);
    return { w: Math.max(1100, maxX + PAD), h: Math.max(540, maxY + PAD) };
  }, [sites]);

  // ---- Node dragging -------------------------------------------------------
  const toSvg = (evt) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };

  const onNodePointerDown = (site) => (evt) => {
    if (readOnly || drawType) return;
    evt.stopPropagation();
    const p = toSvg(evt);
    dragRef.current = { code: site.site_code, dx: p.x - site.map_x, dy: p.y - site.map_y, moved: false };
    evt.currentTarget.setPointerCapture?.(evt.pointerId);
  };

  const onPointerMove = (evt) => {
    const d = dragRef.current;
    if (!d) return;
    const p = toSvg(evt);
    d.moved = true;
    setSites((prev) => prev.map((s) => (s.site_code === d.code
      ? { ...s, map_x: Math.max(0, p.x - d.dx), map_y: Math.max(0, p.y - d.dy) }
      : s)));
  };

  const onPointerUp = () => {
    if (dragRef.current?.moved) setDirtyLayout(true);
    dragRef.current = null;
  };

  const saveLayout = async () => {
    setBusy(true);
    try {
      await planningService.saveSitePositions(
        sites.map((s) => ({ site_code: s.site_code, map_x: Math.round(s.map_x), map_y: Math.round(s.map_y) }))
      );
      setDirtyLayout(false);
      setToast(`Layout saved for ${selectedVersion}.`);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- Drawing a lane ------------------------------------------------------
  const startDraw = (type) => {
    setDrawType((cur) => (cur === type ? null : type));
    setDrawFrom(null);
    setSelectedLane(null);
  };

  const onNodeClick = (site) => async (evt) => {
    evt.stopPropagation();
    if (!drawType) return;
    if (!drawFrom) { setDrawFrom(site.site_code); return; }
    if (drawFrom === site.site_code) { setDrawFrom(null); return; }

    setBusy(true);
    setError('');
    try {
      await planningService.addLane({
        origin_site_code: drawFrom, dest_site_code: site.site_code, lane_type: drawType,
      });
      setToast(`${drawType === 'RETURNS' ? 'Returns' : 'Goods'} lane ${drawFrom} → ${site.site_code} created.`);
      setDrawFrom(null);
      setDrawType(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setDrawFrom(null);
    } finally {
      setBusy(false);
    }
  };

  const removeLane = async (laneId) => {
    setBusy(true);
    try {
      await planningService.deleteLane(laneId);
      setSelectedLane(null);
      setToast('Lane removed.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- Edge geometry -------------------------------------------------------
  // Edges bow to one side so an out-and-back pair (goods down, returns up)
  // draws as two visible arcs rather than one line with two arrowheads.
  //
  // The direction chevron sits at the true curve midpoint, not the control
  // point: for a quadratic Bezier that is 0.25*P0 + 0.5*C + 0.25*P2, and the
  // tangent there is simply P2 - P0.
  const edges = useMemo(() => lanes.map((l) => {
    const a = siteByCode[l.origin_site_code];
    const b = siteByCode[l.dest_site_code];
    if (!a || !b) return null;
    const x1 = a.map_x + NODE_W / 2;
    const y1 = a.map_y + NODE_H / 2;
    const x2 = b.map_x + NODE_W / 2;
    const y2 = b.map_y + NODE_H / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const bow = 30;
    const cx = (x1 + x2) / 2 - (dy / len) * bow;
    const cy = (y1 + y2) / 2 + (dx / len) * bow;
    const midX = 0.25 * x1 + 0.5 * cx + 0.25 * x2;
    const midY = 0.25 * y1 + 0.5 * cy + 0.25 * y2;
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    return {
      lane: l, midX, midY, angle,
      d: `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`,
    };
  }).filter(Boolean), [lanes, siteByCode]);

  const laneVolume = (l) => (l.lane_type === 'RETURNS' ? l.returns_qty : l.qty_capacity_uom);
  const lanePlanned = (l) => (l.lane_type === 'RETURNS' ? l.returns_qty > 0 : l.sku_count > 0);

  const normalLanes = lanes.filter((l) => l.lane_type === 'NORMAL');
  const returnLanes = lanes.filter((l) => l.lane_type === 'RETURNS');
  const shortfalls = sites.filter((s) => (flow[s.site_code]?.balance_qty ?? 0) < -0.5);
  const unconvertible = lanes.reduce((a, l) => a + l.unconvertible_skus, 0);
  const totalProduction = sites.reduce((a, s) => a + (flow[s.site_code]?.production_qty || 0), 0);

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to plan the network.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Network Planning</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Draw the lanes product moves down, then plan the volume on each. Vehicles, distances and cost follow on
              Inter-Depot Transport.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className={btn} onClick={() => setShowUpload(true)} disabled={readOnly || busy}>
              <Upload className="w-3.5 h-3.5" /> Upload volumes
            </button>
            {/* Wired. Proposes a min-cost plan and applies it lane by lane —
                it never writes without an explicit tick. Read-only while the
                site selector is scoped, like every other planning write. */}
            <button className={btn} onClick={() => setShowOptimise(true)} disabled={busy}>
              <Sparkles className="w-3.5 h-3.5" /> Optimise
            </button>
            <button className={drawType === 'NORMAL' ? btnPrimary : btn}
                    onClick={() => startDraw('NORMAL')} disabled={readOnly || busy}>
              <Plus className="w-3.5 h-3.5" /> Goods lane
            </button>
            <button className={drawType === 'RETURNS' ? btnAmber : btn}
                    onClick={() => startDraw('RETURNS')} disabled={readOnly || busy}>
              <Undo2 className="w-3.5 h-3.5" /> Returns lane
            </button>
            {dirtyLayout && (
              <button className={btnPrimary} onClick={saveLayout} disabled={busy}>
                <Save className="w-3.5 h-3.5" /> Save layout
              </button>
            )}
          </div>
        </div>

        {readOnly && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              Viewing as <strong>{selectedSite}</strong>. A network spans every site, so it is read-only from here —
              switch the site selector to <strong>All Sites</strong> to draw lanes or plan volume.
            </p>
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {toast && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <p className="m-0 text-xs text-emerald-700 dark:text-emerald-300">{toast}</p>
          </div>
        )}
      </div>

      <div className="shrink-0 px-6 pb-3 flex flex-wrap items-center gap-2">
        <Stat label="Sites" value={fmt(sites.length)} />
        <Stat label="Goods lanes" value={fmt(normalLanes.length)} />
        <Stat label="Returns lanes" value={fmt(returnLanes.length)} />
        <Stat label="Production (units)" value={fmt(totalProduction)} hint="Total production volume across every plant, this version" />
        <Stat label={`Planned (${capacityUom.toLowerCase()})`} value={fmt(lanes.reduce((a, l) => a + laneVolume(l), 0))} />
        <Stat
          label="Sites short"
          value={fmt(shortfalls.length)}
          tone={shortfalls.length ? 'bad' : 'good'}
          hint={shortfalls.length ? shortfalls.map((s) => s.site_code).join(', ') : 'Every site is supplied'}
        />
        {unconvertible > 0 && (
          <Stat label="Unconvertible SKUs" value={fmt(unconvertible)} tone="warn"
                hint={`No ${capacityUom} on the item master — these drop out of the load figures.`} />
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 flex gap-4">
        <div className="flex-1 min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-auto relative">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : sites.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6">
              <Waypoints className="w-8 h-8 text-slate-300 dark:text-slate-600" />
              <p className="m-0 text-sm text-slate-500 dark:text-slate-400">No sites are on the network.</p>
              <p className="m-0 text-xs text-slate-400">
                Set a site type other than Office in Master Data → Sites to put one on the map.
              </p>
            </div>
          ) : (
            <svg
              ref={svgRef}
              viewBox={`0 0 ${extent.w} ${extent.h}`}
              className="w-full"
              style={{ minHeight: '100%', touchAction: 'none' }}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              onClick={() => { setSelectedLane(null); setDrawFrom(null); }}
            >
              <defs>
                {['#94a3b8', '#2563eb', '#d97706'].map((c, i) => (
                  <marker key={c} id={`arrow${i}`} viewBox="0 0 10 10" refX="9" refY="5"
                          markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
                  </marker>
                ))}
              </defs>

              {edges.map(({ lane, midX, midY, angle, d }) => {
                const sel = selectedLane?.lane_id === lane.lane_id;
                const planned = lanePlanned(lane);
                const isReturn = lane.lane_type === 'RETURNS';
                const colour = sel ? '#2563eb'
                  : planned ? LANE_COLOUR[lane.lane_type] : LANE_COLOUR_EMPTY[lane.lane_type];
                const markerId = sel ? 'arrow1' : isReturn ? 'arrow2' : 'arrow0';
                return (
                  <g key={lane.lane_id} className="cursor-pointer"
                     onClick={(e) => { e.stopPropagation(); setSelectedLane(lane); }}>
                    {/* Fat invisible hit area — a 2px line is unclickable */}
                    <path d={d} stroke="transparent" strokeWidth="20" fill="none" />
                    <path
                      d={d}
                      stroke={colour}
                      strokeWidth={sel ? 2.5 : 2}
                      strokeDasharray={planned ? (isReturn ? '9 4' : undefined) : '5 4'}
                      fill="none"
                      markerEnd={`url(#${markerId})`}
                    />
                    {/* Direction chevron at the curve midpoint, pointing along
                        travel — the end arrowhead alone is easy to miss when a
                        node overlaps it. */}
                    <g transform={`translate(${midX}, ${midY}) rotate(${angle})`} pointerEvents="none">
                      <path d="M -5 -5 L 5 0 L -5 5 Z" fill={colour} />
                    </g>
                    <g transform={`translate(${midX}, ${midY - 18})`} pointerEvents="none">
                      <rect x="-46" y="-10" width="92" height="20" rx="10"
                            fill={sel ? '#2563eb' : '#f8fafc'} stroke={sel ? '#2563eb' : '#e2e8f0'} />
                      <text textAnchor="middle" y="4" fontSize="10.5" fontWeight="600"
                            fill={sel ? '#ffffff' : '#475569'}>
                        {planned ? `${fmt(laneVolume(lane))} ${capacityUom.toLowerCase()}` : isReturn ? 'returns' : 'no volume'}
                      </text>
                    </g>
                  </g>
                );
              })}

              {sites.map((s) => {
                const st = styleOf(s.site_type_code);
                const f = flow[s.site_code] || {};
                const bal = f.balance_qty || 0;
                const prod = f.production_qty || 0;
                const short = bal < -0.5;
                const isFrom = drawFrom === s.site_code;
                return (
                  <g
                    key={s.site_code}
                    transform={`translate(${s.map_x}, ${s.map_y})`}
                    onPointerDown={onNodePointerDown(s)}
                    onClick={onNodeClick(s)}
                    style={{ cursor: drawType ? 'crosshair' : readOnly ? 'default' : 'grab' }}
                  >
                    <rect width={NODE_W} height={NODE_H} rx="10" fill="#ffffff"
                          stroke={isFrom ? '#2563eb' : short ? '#dc2626' : '#e2e8f0'}
                          strokeWidth={isFrom || short ? 2 : 1} />
                    <rect width="5" height={NODE_H} rx="2.5" fill={st.fill} />
                    <text x="15" y="20" fontSize="12" fontWeight="700" fill="#0f172a">
                      {s.site_name.length > 22 ? `${s.site_name.slice(0, 21)}…` : s.site_name}
                    </text>
                    <text x="15" y="34" fontSize="10" fill="#94a3b8">{s.site_code} · {st.label}</text>
                    {/* Production is shown on any site that makes something, so
                        a plant reads as a source at a glance rather than only
                        via its balance. */}
                    {s.can_produce && (
                      <text x="15" y="49" fontSize="10" fontWeight="600" fill="#059669">
                        Produces {fmt(prod)}
                      </text>
                    )}
                    <text x="15" y={s.can_produce ? 63 : 52} fontSize="10" fontWeight="600"
                          fill={short ? '#dc2626' : '#64748b'}>
                      {short ? `Short ${fmt(Math.abs(bal))}` : `Balance ${fmt(bal)}`}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}

          {drawType && (
            <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-white text-xs font-medium shadow-lg ${drawType === 'RETURNS' ? 'bg-amber-600' : 'bg-blue-600'}`}>
              {drawType === 'RETURNS' ? 'Returns lane: ' : 'Goods lane: '}
              {drawFrom ? `from ${drawFrom} — now click where it goes` : 'click where it starts'}
            </div>
          )}
        </div>

        <div className="w-72 shrink-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 overflow-y-auto">
          {selectedLane ? (
            <LaneInspector
              lane={selectedLane}
              siteByCode={siteByCode}
              capacityUom={capacityUom}
              readOnly={readOnly}
              busy={busy}
              onDelete={() => removeLane(selectedLane.lane_id)}
              onOpen={() => setOpenLane(selectedLane)}
            />
          ) : (
            <Legend />
          )}
        </div>
      </div>

      {showUpload && (
        <UploadVolumesModal
          onClose={() => setShowUpload(false)}
          onDone={async (msg) => { setShowUpload(false); setToast(msg); await load(); }}
        />
      )}

      {openLane && openLane.lane_type === 'NORMAL' && (
        <LaneVolumesModal
          laneId={openLane.lane_id}
          readOnly={readOnly}
          fyStartMonth={fyStartMonth}
          onClose={() => setOpenLane(null)}
          onSaved={async (msg) => { setOpenLane(null); setToast(msg); await load(); }}
        />
      )}

      {openLane && openLane.lane_type === 'RETURNS' && (
        <LaneReturnsModal
          laneId={openLane.lane_id}
          readOnly={readOnly}
          fyStartMonth={fyStartMonth}
          onClose={() => setOpenLane(null)}
          onSaved={async (msg) => { setOpenLane(null); setToast(msg); await load(); }}
        />
      )}

      {showOptimise && (
        <OptimiseModal
          onClose={() => setShowOptimise(false)}
          capacityUom={capacityUom}
          onApplied={load}
        />
      )}
    </div>
  );
}

// ---- Summary chip -----------------------------------------------------------
const Stat = ({ label, value, tone, hint }) => {
  const toneCls = tone === 'bad'
    ? 'border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10'
    : tone === 'warn'
      ? 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10'
      : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800';
  return (
    <div className={`px-3 py-1.5 rounded-lg border ${toneCls}`} title={hint || ''}>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="ml-2 text-sm font-bold text-slate-800 dark:text-slate-100">{value}</span>
    </div>
  );
};

// ---- Lane inspector ---------------------------------------------------------
// What the lane IS and what is on it. No vehicle, no distance, no return factor
// — those are rating attributes and live on Inter-Depot Transport.
const LaneInspector = ({ lane, siteByCode, capacityUom, readOnly, busy, onDelete, onOpen }) => {
  const o = siteByCode[lane.origin_site_code];
  const d = siteByCode[lane.dest_site_code];
  const isReturn = lane.lane_type === 'RETURNS';

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">Lane</p>
          <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${isReturn
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>
            {isReturn ? 'Returns' : 'Goods'}
          </span>
        </div>
        <p className="m-0 mt-1.5 text-sm font-bold text-slate-800 dark:text-slate-100">
          {o?.site_name || lane.origin_site_code}
        </p>
        <p className="m-0 text-base leading-none text-slate-400">↓</p>
        <p className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">
          {d?.site_name || lane.dest_site_code}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        {isReturn ? (
          <Metric label={capacityUom.toLowerCase()} value={fmt(lane.returns_qty)} />
        ) : (
          <>
            <Metric label="SKUs" value={fmt(lane.sku_count)} />
            <Metric label={capacityUom.toLowerCase()} value={fmt(lane.qty_capacity_uom)} />
          </>
        )}
      </div>

      {lane.unconvertible_skus > 0 && (
        <p className="m-0 text-[11px] text-amber-600 dark:text-amber-400">
          {lane.unconvertible_skus} value(s) have no {capacityUom} on the item master and are excluded from the
          {' '}{capacityUom.toLowerCase()} figure.
        </p>
      )}

      <p className="m-0 text-[11px] text-slate-400">
        {isReturn
          ? 'Returns carry a volume, not a SKU plan — a return is a load, not a product line.'
          : 'Vehicle, distance and whether the truck comes back empty are set on Inter-Depot Transport.'}
      </p>

      <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
        <button className={isReturn ? btnAmber : btnPrimary} onClick={onOpen} disabled={busy}>
          {isReturn ? 'Plan returns' : 'Assign SKUs'}
        </button>
        <button className={btn} onClick={onDelete} disabled={readOnly || busy} title="Remove lane and its volumes">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div className="px-2 py-1.5 rounded-md bg-slate-50 dark:bg-slate-900/50">
    <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    <span className="block text-sm font-bold text-slate-800 dark:text-slate-100">{value}</span>
  </div>
);

const Legend = () => (
  <div className="text-xs text-slate-500 dark:text-slate-400 space-y-3">
    <p className="m-0 font-bold uppercase tracking-wider text-slate-400">Sites</p>
    <div className="space-y-1.5">
      {Object.entries(TYPE_STYLE).map(([code, st]) => (
        <div key={code} className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded" style={{ background: st.fill }} />
          <span>{st.label}</span>
        </div>
      ))}
    </div>
    <div className="pt-2 border-t border-slate-100 dark:border-slate-700 space-y-1.5">
      <p className="m-0 font-bold uppercase tracking-wider text-slate-400">Lanes</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-slate-400 align-middle mr-1.5" /> Goods, volume planned</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-dashed border-amber-500 align-middle mr-1.5" /> Returns</p>
      <p className="m-0"><span className="inline-block w-6 border-t-2 border-dashed border-slate-300 align-middle mr-1.5" /> No volume yet</p>
      <p className="m-0">The chevron mid-lane points the way the stock travels.</p>
    </div>
    <p className="m-0 pt-2 border-t border-slate-100 dark:border-slate-700">
      Click a lane to inspect it, or drag a site to rearrange the map.
      <strong className="block mt-1 text-slate-600 dark:text-slate-300">Balance</strong>
      production + inbound − outbound − local demand. Negative means the plan cannot supply that site.
      Returns are excluded — a crate coming back is not supply of a SKU.
    </p>
  </div>
);
